import { getPartners, NavigationProps } from '@amf/shared/utils/navigation';
import { NewsDetailItemFragment } from '@amf/shared/types/graphql';
import { GetServerSideProps } from 'next';
import { TFnType, useTranslate } from '@tolgee/react';
import Link from 'next/link';
import { format, fromUnixTime } from 'date-fns';
import Container from '@amf/shared/components/layout/Container';
import Button from '@amf/shared/components/button/Button';
import IconArrowLeft from '@amf/shared/icons/IconArrowLeft';
import Content from '@amf/shared/components/content/Content';
import { getLanguageLocale } from '@amf/shared/utils/languages';
import { sdk } from '@amf/shared/lib/graphQlClient';

export type Props = NavigationProps & {
  news: NewsDetailItemFragment;
};

type PageProps = Omit<Props, 'navigation' | 'partners'> & {
  prefixes: { newsList: string };
  translationNamespaces?: string | string[];
};

function NewsDetailPage({ news, prefixes, translationNamespaces }: PageProps) {
  const { t } = useTranslate(translationNamespaces);
  const publishedAt = fromUnixTime(news.publishedAt);
  const author = getNewsAuthor(news, t);
  return (
    <Container>
      <Link href={prefixes.newsList}>
        <a>
          <Button variant='blue-outline' leftIcon={<IconArrowLeft />}>
            {t('news.detail.back')}
          </Button>
        </a>
      </Link>
      <h1 className='NewsDetailPage__title'>{news.title}</h1>
      <p className='NewsDetailPage__subtitle'>
        <span>{format(publishedAt, 'dd/MM/yyyy, HH:mm')}</span>
        {news.categories.map(category => {
          if (!category) {
            return null;
          }

          return <span key={category.id}>{category.name}</span>;
        })}
        <span>{news.union.name}</span>
        <span>
          {t('news.detail.author')}: {author}
        </span>
      </p>
      <p className='NewsDetailPage__perex'>{news.perex}</p>
      <Content content={news.content} />
    </Container>
  );
}

export default NewsDetailPage;

export const getServerSideProps: GetServerSideProps<Props, { path: string }> = async ({
  locale,
  params,
}) => {
  try {
    const { news, navigation } = await sdk.NewsDetailPage({
      path: params.path,
      union: process.env.NEXT_PUBLIC_UNION,
      locale: getLanguageLocale(locale),
    });

    if (!news) return { notFound: true };
    return {
      props: {
        news,
        navigation,
        partners: await getPartners(),
      },
    };
  } catch (e) {
    console.error(e);
    return { notFound: true };
  }
};

export function getNewsAuthor(news: NewsDetailItemFragment, t: TFnType): string {
  if (news.author.custom) {
    return news.author.custom;
  }

  if (news.author.internal) {
    return news.author.internal.givenName + ' ' + news.author.internal.familyName;
  }

  return t('news.detail.redaction');
}
